/* eslint-disable @typescript-eslint/no-empty-function */

import React, {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Bugsnag from '@bugsnag/js';
import { AlertProvider } from '@99designs/design-services-layouts';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { useProductOptionsPricingQuery, useRequestQuery } from '../../graph';
import { RequestQuery } from '../../graph/GetRequest.generated';
import { Configuration, configure, getDefault } from '../../lib/config';
import { LogomakerAsset } from '../BriefForm';
import { RecentlySelectedProduct } from '../BriefForm/FormFields/ClientConfiguredFields/ProductDropdown/utils';
import {
    BriefProductFragment,
    BriefQuery,
    BriefRequestFragment,
    StructuredBriefFragment,
} from '../BriefForm/brief.generated';
import { isBifrostBrief } from './isBifrostBrief';

export type RequestExpertService = NonNullable<RequestQuery['request']['expertService']>;

const MAX_RECENT_PRODUCTS = 16;

export type NormalisedBriefProduct = Omit<
    BriefProductFragment,
    'fulfilmentStrategy' | 'name' | '__typename' | 'pricing' | 'key' | 'cimpressProductConfig'
> & {
    pricing: Omit<BriefProductFragment['pricing'], 'earningsInUsd' | 'amount'> & {
        earningsInUsd?: number;
        taxed: number;
        untaxed: number;
        currency: string;
    };
    key: string | null;
};

export type Brief = {
    request: BriefRequestFragment | null;
    product: BriefProductFragment | null;
} & StructuredBriefFragment;

interface BriefContextProps {
    product?: NormalisedBriefProduct;
    expertService?: RequestExpertService;
    isPricingLoading: boolean;
    isRequestQueryLoading: boolean;
    updateProduct: (product?: NormalisedBriefProduct) => void;
    onCompleted: (data: BriefQuery) => void;
    recentProducts?: RecentlySelectedProduct[];
    addRecentProduct: (product: RecentlySelectedProduct) => void;
    isUpdatingProduct: boolean;
    setIsUpdatingProduct: React.Dispatch<React.SetStateAction<boolean>>;
    selectedProductOptions: Record<string, string>;
    config: Configuration;
    correlationId: BriefQuery['brief']['correlationId'];
    serviceProductMpvId: string;
    serviceProductOptions?: ReadonlyArray<{ key: string; value: string }> | null;
    logomakerAsset?: LogomakerAsset;
    reportedIssues?: string[] | null;
    isRequestClaimed?: boolean | null;
    isBifrostBrief?: boolean;
    workId?: string;
    requestExperiments?: string[] | null;
    isEnrolledInIbpoTest: boolean;
}

export const BriefContext = createContext<BriefContextProps | undefined>(undefined);

interface BriefProviderProps {
    children: ReactNode;
}

export const BriefProvider: React.FC<BriefProviderProps> = ({ children }) => {
    const [product, setProduct] = useState<NormalisedBriefProduct>();
    const [selectedProductOptions, setSelectedProductOptions] = useState<Record<string, string>>(
        {}
    );
    const [config, setConfig] = useState<Configuration>(getDefault());
    const [briefData, setBriefData] = useState<BriefQuery['brief'] | null>(null);

    // Get request data for dynamic pricing (only if we have a request ID)
    const requestId = briefData?.request?.id;
    const { data: requestData, loading: isRequestQueryLoading } = useRequestQuery({
        variables: { id: requestId || '' },
        skip: !requestId,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
    });

    const expertService = requestData?.request?.expertService ?? undefined;

    const logomakerEntityId = briefData?.request?.optionalFacts?.logomakerEntityId;
    const logomakerEntitySig = briefData?.request?.optionalFacts?.logomakerEntitySig;
    const logomakerAsset =
        logomakerEntityId && logomakerEntitySig
            ? { id: logomakerEntityId, sig: logomakerEntitySig }
            : undefined;
    const reportedIssues = briefData?.request?.optionalFacts?.customerDocument?.reportedIssues;

    // Convert serviceProductOptions to selections format for pricing query
    const pricingSelections = useMemo(() => {
        if (!requestData?.request?.facts?.serviceProductOptions) {
            return [];
        }
        return requestData.request.facts.serviceProductOptions.map(({ key, value }) => ({
            key,
            value,
        }));
    }, [requestData?.request?.facts?.serviceProductOptions]);

    // Get service product details for pricing query
    const serviceProduct = requestData?.request?.expertService?.serviceProduct;
    const shouldFetchPricing = serviceProduct?.productKey && pricingSelections.length > 0;

    // Call pricing query with serviceProductOptions
    const {
        data: pricingData,
        loading: pricingLoading,
        error: pricingError,
    } = useProductOptionsPricingQuery({
        variables: {
            input: {
                productKey: serviceProduct?.productKey || '',
                pimMerchantVersion: serviceProduct?.pimMerchantVersion || 1,
                selections: pricingSelections,
                pivotSelections: [],
            },
        },
        skip: !shouldFetchPricing,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
    });

    // TODO: Recent products should be stored in the browser's local storage.

    const [recentProducts, setRecentProducts] = useState<RecentlySelectedProduct[]>([]);

    const updateProduct = useCallback(
        (newProduct?: NormalisedBriefProduct) => {
            setProduct(newProduct);
        },
        [setProduct]
    );

    const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);

    // Runs onCompleted once per provider instance. It is consumed from an effect with an
    // unstable identity, so without this guard it re-enters every render and never terminates
    // for briefs that cannot derive a product.
    const hasCompletedRef = useRef(false);

    const onCompleted = useCallback(
        (data: BriefQuery) => {
            if (hasCompletedRef.current || !data) {
                return;
            }
            hasCompletedRef.current = true;

            setConfig(configure(data.brief.config));
            setBriefData(data.brief);

            const prod = formatProductFromProdOrService(data.brief);
            if (prod) {
                updateProduct(prod);
            }
            const productOptions = data.brief.dynamicFields.find(
                (df) => df.id === 'productOptions'
            );
            if (!productOptions) {
                return;
            }
            if (productOptions.__typename !== 'ClientConfiguredField') {
                Bugsnag.notify('Expected productOption to be a ClientConfiguredField');
                return;
            }
            if (!productOptions.clientConfiguredValue) {
                return;
            }
            try {
                const parsedValue = JSON.parse(productOptions.clientConfiguredValue);
                setSelectedProductOptions(parsedValue);
            } catch (e) {
                Bugsnag.notify('Failed to parse product options');
            }
        },
        [updateProduct]
    );

    // Memoize the new pricing values only when they actually change and differ from current product
    const newPricingValues = useMemo(() => {
        if (pricingLoading || !product || !pricingData?.selectionsPricing || pricingError) {
            return null;
        }

        const newTaxed = pricingData.selectionsPricing.listPriceCents.taxed;
        const newUntaxed = pricingData.selectionsPricing.listPriceCents.untaxed;
        const newCurrency = pricingData.selectionsPricing.currency;

        const hasCurrentPricing =
            product.pricing.taxed === newTaxed &&
            product.pricing.untaxed === newUntaxed &&
            product.pricing.currency === newCurrency;

        if (hasCurrentPricing) {
            return null;
        }

        return {
            taxed: newTaxed,
            untaxed: newUntaxed,
            currency: newCurrency,
        };
    }, [pricingLoading, pricingData, pricingError, product]);

    // Update product with dynamic pricing when new pricing values are available
    useEffect(() => {
        if (!newPricingValues || !product) {
            return;
        }

        const updatedProduct = {
            ...product,
            pricing: {
                ...product.pricing,
                taxed: newPricingValues.taxed,
                untaxed: newPricingValues.untaxed,
                currency: newPricingValues.currency,
            },
        };

        updateProduct(updatedProduct);
    }, [newPricingValues, updateProduct]);

    const serviceProductMpvId = requestData?.request.expertService?.serviceProduct?.mpvId || '';
    const requestExperiments = requestData?.request?.facts?.experiments;

    const inIbpoTestVariant = useInVistaExperimentVariant('ds_sdp_ibpo');
    const isEnrolledInIbpoTest = inIbpoTestVariant('test') === true;

    return (
        <BriefContext.Provider
            value={{
                product,
                expertService,
                isRequestQueryLoading,
                recentProducts,
                selectedProductOptions,
                config,
                logomakerAsset,
                reportedIssues,
                isPricingLoading: pricingLoading,
                isRequestClaimed: requestData?.request.isClaimed,
                isBifrostBrief: briefData
                    ? isBifrostBrief(briefData as StructuredBriefFragment)
                    : undefined,
                workId: briefData?.request?.facts?.workId ?? undefined,
                correlationId: briefData?.correlationId ?? '',
                serviceProductMpvId,
                serviceProductOptions: requestData?.request?.facts?.serviceProductOptions,
                requestExperiments,
                isEnrolledInIbpoTest,
                updateProduct,
                addRecentProduct: (product: RecentlySelectedProduct) => {
                    setRecentProducts((prev) => {
                        if (prev.length >= MAX_RECENT_PRODUCTS) {
                            prev.splice(MAX_RECENT_PRODUCTS - 1, 1);
                        }

                        const index = prev.findIndex((p) => p.mpvId === product.mpvId);

                        if (index !== -1) {
                            prev.splice(index, 1);
                        }

                        return [product, ...prev];
                    });
                },
                onCompleted,
                isUpdatingProduct,
                setIsUpdatingProduct,
            }}
        >
            <AlertProvider>{children}</AlertProvider>
        </BriefContext.Provider>
    );
};

export function formatProductFromProdOrService(
    brief: {
        request: BriefRequestFragment | null;
        product: BriefProductFragment | null;
    } & StructuredBriefFragment
): NormalisedBriefProduct | null {
    if (brief.product && isBifrostBrief(brief)) {
        const amountInCents = brief.product.pricing.amount.amountInCents;
        const currency = brief.product.pricing.amount.currency;
        return {
            title: brief.product.title,
            details: brief.product.details,
            pricing: {
                __typename: 'FixedPrice' as const,
                earningsInUsd: brief.product.pricing.earningsInUsd,
                taxed: amountInCents,
                untaxed: amountInCents,
                currency,
            },
            key: brief.product.key,
            fulfilmentMpvId: null,
        };
    }
    if (brief.product && brief.fulfilment) {
        const amountInCents = brief.product.pricing.amount.amountInCents;
        const currency = brief.product.pricing.amount.currency;
        return {
            title: brief.product.title,
            details: brief.product.details,
            pricing: {
                __typename: 'FixedPrice' as const,
                earningsInUsd: brief.product.pricing.earningsInUsd,
                taxed: amountInCents,
                untaxed: amountInCents,
                currency,
            },
            key: brief.fulfilment.productKey,
            fulfilmentMpvId: brief.fulfilment.mpvId,
        };
    }
    return expertServiceToProduct(brief.request ?? undefined);
}

export function MockBriefProvider({
    children,
    value = {},
}: {
    children: ReactNode;
    value?: Partial<BriefContextProps>;
}) {
    return (
        <BriefContext.Provider
            value={{
                product: {
                    title: 'Mock Product',
                    details: [],
                    pricing: {
                        __typename: 'FixedPrice',
                        taxed: 1000,
                        untaxed: 930,
                        currency: 'USD',
                    },
                    key: 'mock-key',
                    fulfilmentMpvId: 'mock-fulfilment-mpv-id',
                },
                isRequestQueryLoading: false,
                isBifrostBrief: false,
                isPricingLoading: false,
                updateProduct: () => {},
                onCompleted: () => {},
                recentProducts: [],
                addRecentProduct: () => {},
                isUpdatingProduct: false,
                setIsUpdatingProduct: () => {},
                selectedProductOptions: {},
                config: getDefault(),
                correlationId: 'mock-correlation-id',
                serviceProductMpvId: '',
                logomakerAsset: undefined,
                reportedIssues: undefined,
                workId: undefined,
                isEnrolledInIbpoTest: false,
                ...value,
            }}
        >
            {children}
        </BriefContext.Provider>
    );
}

export function expertServiceToProduct(
    request?: BriefRequestFragment
): NormalisedBriefProduct | null {
    const es = request?.expertService;

    if (!request || !es?.printProduct) {
        return null;
    }

    const earnings = request.facts?.expertEarningsInUsdCents || 0;
    const taxed = es.price.listPriceCents.taxed;
    const untaxed = es.price.listPriceCents.untaxed;

    return {
        title: es.title,
        details:
            es.productDetails?.inclusions.map((i) => ({
                __typename: 'ProductPackageDetails',
                title: i.description,
            })) ?? [],
        pricing: {
            __typename: 'FixedPrice',
            earningsInUsd: earnings,
            taxed,
            untaxed,
            currency: es.price.currency,
        },
        key: es.printProduct.mpvId,
        fulfilmentMpvId: es.printProduct.mpvId,
    };
}

export const useBriefContext = () => {
    const context = useContext(BriefContext);

    if (!context) {
        throw new Error('useBriefContext must be used within a BriefProvider');
    }

    return context;
};

/**
 * Updates the product in the brief context when the brief changes
 */
export const useUpdateProductOnBriefChange = (brief?: Brief) => {
    const { updateProduct, product } = useBriefContext();

    useEffect(() => {
        if (!brief) {
            return;
        }
        const briefProduct = formatProductFromProdOrService(brief);
        if (!briefProduct || areTheSameProducts(briefProduct, product)) {
            // Don't update to avoid unnecessary re-renders
            return;
        }
        updateProduct(briefProduct);
    }, [brief, updateProduct, product]);
};

function areTheSameProducts(
    productA: NormalisedBriefProduct | null | undefined,
    productB: NormalisedBriefProduct | null | undefined
) {
    if (!productA || !productB) return productA === productB;
    const { pricing: _a, ...restA } = productA;
    const { pricing: _b, ...restB } = productB;
    return JSON.stringify(restA) === JSON.stringify(restB);
}
